import React, { useState, useEffect } from 'react';
import { XMarkIcon, ArrowDownTrayIcon, PhotoIcon, MagnifyingGlassPlusIcon, MagnifyingGlassMinusIcon } from '@heroicons/react/24/outline';
import { learningAPI } from '../../services/api';

interface ImageViewerProps {
  isOpen: boolean;
  onClose: () => void;
  content: {
    id: string;
    title: string;
    fileUrl: string;
    fileName?: string;
    section?: {
      name: string;
      team: {
        name: string;
      };
    };
    createdAt: string;
  };
}

const ImageViewer: React.FC<ImageViewerProps> = ({ isOpen, onClose, content }) => {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [zoom, setZoom] = useState(1);
  const [imageUrl, setImageUrl] = useState<string>('');

  useEffect(() => {
    if (isOpen && content.id) {
      setIsLoading(true);
      setError(null);
      learningAPI.getFileBlob(content.id)
        .then(url => {
          setImageUrl(url);
          setIsLoading(false);
        })
        .catch(err => {
          console.error('Error loading image:', err);
          setError(err.message || 'Failed to load image.');
          setIsLoading(false);
        });
    }

    // Cleanup blob URL when component unmounts or content changes
    return () => {
      if (imageUrl) {
        window.URL.revokeObjectURL(imageUrl);
      }
    };
  }, [isOpen, content.id]);

  const handleImageLoad = () => {
    setIsLoading(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setError('Failed to load image.');
  };

  const handleDownload = async () => {
    try {
      await learningAPI.downloadFile(content.id, content.fileName || `${content.title}.jpg`);
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.25));
  };

  const handleResetZoom = () => {
    setZoom(1);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-6xl sm:w-full">
          {/* Header */}
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <PhotoIcon className="h-6 w-6 text-green-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">
                    {content.title}
                  </h3>
                  {content.section && (
                    <p className="text-sm text-gray-500">
                      {content.section.name} • {content.section.team.name}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center space-x-2">
                {/* Zoom controls */}
                <div className="flex items-center space-x-1 bg-gray-100 rounded-md p-1">
                  <button
                    onClick={handleZoomOut}
                    disabled={zoom <= 0.25}
                    className="p-1 rounded text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Zoom Out"
                  >
                    <MagnifyingGlassMinusIcon className="h-4 w-4" />
                  </button>
                  <button
                    onClick={handleResetZoom}
                    className="px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-800"
                    title="Reset Zoom"
                  >
                    {Math.round(zoom * 100)}%
                  </button>
                  <button
                    onClick={handleZoomIn}
                    disabled={zoom >= 3}
                    className="p-1 rounded text-gray-600 hover:text-gray-800 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Zoom In"
                  >
                    <MagnifyingGlassPlusIcon className="h-4 w-4" />
                  </button>
                </div>
                <button
                  onClick={handleDownload}
                  className="bg-primary-600 text-white rounded-md p-2 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                  title="Download Image"
                >
                  <ArrowDownTrayIcon className="h-5 w-5" />
                </button>
                <button
                  onClick={onClose}
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
            </div>
          </div>

          {/* Image Viewer */}
          <div className="px-4 pb-4 sm:px-6 sm:pb-6">
            <div className="relative w-full bg-gray-100 rounded-lg overflow-auto" style={{ height: '70vh' }}>
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex flex-col items-center space-y-2">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                    <p className="text-sm text-gray-500">Loading image...</p>
                  </div>
                </div>
              )}

              {error ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="text-center p-6">
                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100">
                      <XMarkIcon className="h-6 w-6 text-red-600" />
                    </div>
                    <h3 className="mt-2 text-sm font-medium text-gray-900">Image Loading Error</h3>
                    <p className="mt-1 text-sm text-gray-500">{error}</p>
                    <div className="mt-4">
                      <button
                        onClick={handleDownload}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                      >
                        <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
                        Download Image
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full p-4">
                  <img
                    src={imageUrl}
                    alt={content.title}
                    className="max-w-full max-h-full object-contain rounded-lg shadow-lg transition-transform duration-200"
                    style={{ 
                      transform: `scale(${zoom})`,
                      cursor: zoom > 1 ? 'grab' : 'default'
                    }}
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    draggable={false}
                  />
                </div>
              )}
            </div>

            {/* File Info */}
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center space-x-4">
                  <span>Added on {new Date(content.createdAt).toLocaleDateString()}</span>
                  {content.fileName && (
                    <span>File: {content.fileName}</span>
                  )}
                </div>
                <button
                  onClick={handleDownload}
                  className="text-primary-600 hover:text-primary-700 font-medium flex items-center space-x-1"
                >
                  <ArrowDownTrayIcon className="h-4 w-4" />
                  <span>Download</span>
                </button>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={onClose}
              className="w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImageViewer;
